import { DateFormat, parseDate, transformDate } from 'shared/entities/date';
import { DateBaseModel } from 'shared/models/components/DatePicker';
import { DateInputDateFormat } from 'shared/newEntities/components/DateInput';

export const mapFormatToTemplate = {
  [DateFormat.dayMonthYear]: 'xx.xx.xxxx',
  [DateFormat.dayMonthYearTime]: 'xx.xx.xxxx xx:xx'
};

export const mapFormatToPlaceholder = {
  [DateFormat.dayMonthYear]: '__.__.____',
  [DateFormat.dayMonthYearTime]: '__.__.____ __:__'
};

export const transformToString = (
  model: DateBaseModel | null,
  format: DateInputDateFormat
): string => {
  if (!model) {
    return '';
  }
  switch (format) {
    case DateFormat.dayMonthYear:
      return transformDate(model.date, DateFormat.dayMonthYearWithoutDots);
    case DateFormat.dayMonthYearTime:
      return transformDate(model.date, DateFormat.dayMonthYearTimeWithoutDots);
  }
};

const validateDate = (value: string): BaseResponse<Date> => {
  const groups = value.match(
    /^(?<day>\d{2})(?<month>\d{2})(?<year>\d{4})$/
  )?.groups;

  if (!groups || !groups.day || !groups.month || !groups.year) {
    return { isError: true };
  }

  const date = parseDate(
    `${groups.day}.${groups.month}.${groups.year}`,
    DateFormat.dayMonthYear
  );
  if (!date) {
    return { isError: true };
  }
  return {
    isError: false,
    data: date
  };
};

export enum DateTimeErrorKind {
  date = 'date',
  time = 'time'
}

const validateDateTime = (
  value: string
): BaseResponse<Date, DateTimeErrorKind> => {
  if (value.length < 8) {
    return { isError: true, data: DateTimeErrorKind.date };
  }

  const dateResult = validateDate(value.slice(0, 8));
  if (dateResult.isError) {
    return { isError: true, data: DateTimeErrorKind.date };
  }

  if (value.length < 12) {
    return { isError: true, data: DateTimeErrorKind.time };
  }

  const hours = Number(value.slice(8, 10));
  const minutes = Number(value.slice(10, 12));
  if (hours > 23 || minutes > 59) {
    return { isError: true, data: DateTimeErrorKind.time };
  }

  const date = parseDate(
    `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)} ${value.slice(8, 10)}:${value.slice(10, 12)}`,
    DateFormat.dayMonthYearTime
  );

  if (!date) {
    return { isError: true, data: DateTimeErrorKind.time };
  }

  return {
    isError: false,
    data: date
  };
};
/*
 * Определяет, является ли строка датой
 * @param value: string
 * @param dateFormat?: DateInputDateFormat - если undefined, поддерживаем оба формата
 * @returns BaseResponse<Date>
 */
export type DateInputValidationResult = BaseResponse<
  { date: Date; format: DateInputDateFormat },
  DateTimeErrorKind
>;

export const validateStringValue = (
  value: string,
  dateFormat?: DateInputDateFormat
): DateInputValidationResult => {
  if (dateFormat) {
    if (dateFormat === DateFormat.dayMonthYear) {
      const dateResult = validateDate(value);
      return dateResult.isError
        ? { isError: true, data: DateTimeErrorKind.date }
        : {
            isError: false,
            data: { date: dateResult.data, format: DateFormat.dayMonthYear }
          };
    }

    const dateTimeResult = validateDateTime(value);
    return dateTimeResult.isError
      ? dateTimeResult
      : {
          isError: false,
          data: {
            date: dateTimeResult.data,
            format: DateFormat.dayMonthYearTime
          }
        };
  }
  const result = validateDate(value);
  if (result.isError) {
    const dateTimeResult = validateDateTime(value);
    return dateTimeResult.isError
      ? dateTimeResult
      : {
          isError: false,
          data: {
            date: dateTimeResult.data,
            format: DateFormat.dayMonthYearTime
          }
        };
  }
  return {
    isError: false,
    data: { date: result.data, format: DateFormat.dayMonthYear }
  };
};
